import React, { useState } from "react";
import {
  MapPin,
  Phone,
  Mail,
  MessageCircle,
  Users,
  Heart,
  Clock,
  Globe,
  CheckCircle,
} from "lucide-react";

import Input from "../components/Input";
import Textarea from "../components/Textarea";
import Select from "../components/Select";
import { useTranslation } from "react-i18next";

import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const Button = ({ children, className = "", ...props }) => (
  <button
    className={`px-4 py-2 rounded font-semibold transition inline-flex items-center justify-center ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Card = ({ children, className = "" }) => (
  <div
    className={`rounded-lg border border-gray-300 bg-white text-black shadow ${className}`}
  >
    {children}
  </div>
);

const CardContent = ({ children, className = "" }) => (
  <div className={className}>{children}</div>
);

/* ============================
   PAGE CONTACT
============================ */

export default function ContactPage() {
  const { t } = useTranslation();

  const [status, setStatus] = useState("idle");

  const contactInfo = [
    {
      icon: MapPin,
      title: t("contact.info.visit.title"),
      details: ["Oulmes , Maroc", "Dar Echabab , دار الشباب"],
      color: "primary",
    },
    {
      icon: Phone,
      title: t("contact.info.call.title"),
      details: [
        "+212 6 17 67 99 66",
        "+212 6 48 54 56 47",
        t("contact.info.call.hours"),
      ],
      color: "secondary",
    },
    {
      icon: Mail,
      title: t("contact.info.email.title"),
      details: ["aleoaassociation@gmail.com", "associationaleoa@gmail.com"],
      color: "electric-blue",
    },
    {
      icon: Clock,
      title: t("contact.info.hours.title"),
      details: [t("contact.info.hours.week"), t("contact.info.hours.sunday")],
      color: "gold",
    },
  ];

  const socialLinks = [
    {
      name: "Facebook",
      url: "https://www.facebook.com/profile.php?id=100069377171465&locale=fr_FR",
      followers: "15K",
    },
    {
      name: "Instagram",
      url: "https://www.instagram.com/association_aleoa/",
      followers: "12K",
    },
    {
      name: "LinkedIn",
      url: "https://linkedin.com/company/leadersofexcellence",
      followers: "20K",
    },
  ];

  const faqs = [
    {
      question: t("contact.faq.q1"),
      answer: t("contact.faq.a1"),
    },
    {
      question: t("contact.faq.q2"),
      answer: t("contact.faq.a2"),
    },
    {
      question: t("contact.faq.q3"),
      answer: t("contact.faq.a3"),
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <section className="py-20 bg-blue-50">
        <div className="container max-w-[100rem] mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl font-black text-foreground mb-6 leading-tight">
              {t("contact.hero.title1")}{" "}
              <span className="text-primary">
                {t("contact.hero.highlight")}
              </span>
            </h1>

            <p className="font-paragraph text-lg sm:text-xl text-foreground/80 max-w-4xl mx-auto leading-relaxed">
              {t("contact.hero.description1")}
              <br/>
              {t("contact.hero.description2")}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container max-w-[100rem] mx-auto px-6">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-foreground mb-4">
              {t("contact.info.title1")}{" "}
              <span className="text-primary">
                {t("contact.info.highlight")}
              </span>
            </h2>

            <p className="font-paragraph text-lg sm:text-xl text-foreground/80 max-w-3xl mx-auto">
              {t("contact.info.description")}
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="text-center p-6 h-full hover:shadow-xl transition-shadow duration-300">
                  <CardContent className="p-0">
                    <info.icon
                      className={`h-12 w-12 text-${info.color} mx-auto mb-4`}
                    />
                    <h3 className="font-heading text-xl font-bold text-foreground mb-3">
                      {info.title}
                    </h3>

                    <div className="space-y-1">
                      {info.details.map((detail, detailIndex) => (
                        <p
                          key={detailIndex}
                          className="font-paragraph text-sm text-foreground/80 break-words"
                        >
                          {detail}
                        </p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-blue-50" id="contact-form">
        <div className="container max-w-[100rem] mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, x: 0 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-center lg:text-left"
            >
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-6">
                {t("contact.form.title1")}{" "}
                <span className="text-secondary">
                  {t("contact.form.highlight")}
                </span>
              </h2>

              <p className="font-paragraph text-base sm:text-lg text-foreground/80 mb-8 leading-relaxed">
                {t("contact.form.description")}
              </p>

              <div className="mt-12 sm:mt-16 grid gap-8 sm:gap-10">
                <div className="flex sm:flex-row flex-col items-center sm:items-start text-center sm:text-left">
                  <MessageCircle className="h-6 w-6 text-primary mr-0 sm:mr-3 mb-2 sm:mb-0" />
                  <div>
                    <h4 className="font-heading font-semibold text-foreground mb-1">
                      {t("contact.form.features.fast.title")}
                    </h4>

                    <p className="font-paragraph text-sm text-foreground/80">
                      {t("contact.form.features.fast.description")}
                    </p>
                  </div>
                </div>

                <div className="flex sm:flex-row flex-col items-center sm:items-start text-center sm:text-left">
                  <Users className="h-6 w-6 text-secondary mr-0 sm:mr-3 mb-2 sm:mb-0" />
                  <div>
                    <h4 className="font-heading font-semibold text-foreground mb-1">
                      {t("contact.form.features.support.title")}
                    </h4>

                    <p className="font-paragraph text-sm text-foreground/80">
                      {t("contact.form.features.support.description")}
                    </p>
                  </div>
                </div>

                <div className="flex sm:flex-row flex-col items-center sm:items-start text-center sm:text-left">
                  <Heart className="h-6 w-6 text-electric-blue mr-0 sm:mr-3 mb-2 sm:mb-0" />
                  <div>
                    <h4 className="font-heading font-semibold text-foreground mb-1">
                      {t("contact.form.features.personal.title")}
                    </h4>

                    <p className="font-paragraph text-sm text-foreground/80">
                      {t("contact.form.features.personal.description")}
                    </p>
                  </div>
                </div>

                <div className="flex sm:flex-row flex-col items-center sm:items-start text-center sm:text-left">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-0 sm:mr-3 mb-2 sm:mb-0" />
                  <div>
                    <h4 className="font-heading font-semibold text-foreground mb-1">
                      {t("contact.form.features.reliable.title")}
                    </h4>

                    <p className="font-paragraph text-sm text-foreground/80">
                      {t("contact.form.features.reliable.description")}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 0 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="w-full"
            >
              <Card className="p-8">
                <CardContent className="p-0">
                  <form
                    className="space-y-6"
                    onSubmit={async (e) => {
                      e.preventDefault();

                      setStatus("loading");

                      const formData = new FormData(e.target);
                      const data = Object.fromEntries(formData.entries());

                      const res = await fetch("/api/contact", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(data),
                      });

                      const result = await res.json();

                      if (result.success) {
                        setStatus("success");
                        e.target.reset();
                      } else {
                        setStatus("error");
                      }
                    }}
                  >
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block font-paragraph font-semibold text-foreground mb-2">
                          {t("contact.form.fields.firstName")}
                        </label>

                        <Input
                          name="prenom"
                          required
                          placeholder={t("contact.form.placeholders.firstName")}
                        />
                      </div>

                      <div>
                        <label className="block font-paragraph font-semibold text-foreground mb-2">
                          {t("contact.form.fields.lastName")}
                        </label>

                        <Input
                          name="nom"
                          required
                          placeholder={t("contact.form.placeholders.lastName")}
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block font-paragraph font-semibold text-foreground mb-2">
                          {t("contact.form.fields.email")}
                        </label>

                        <Input
                          name="email"
                          type="email"
                          required
                          placeholder="email@example.com"
                        />
                      </div>

                      <div>
                        <label className="block font-paragraph font-semibold text-foreground mb-2">
                          {t("contact.form.fields.phone")}
                        </label>

                        <Input name="telephone" placeholder="+212..." />
                      </div>
                    </div>

                    <div>
                      <label className="block font-paragraph font-semibold text-foreground mb-2">
                        {t("contact.form.fields.subject")}
                      </label>

                      <select
                        name="sujet"
                        required
                        className="w-full p-3 border rounded-lg"
                      >
                        <option value="">
                          {t("contact.form.subjects.select")}
                        </option>

                        <option value="general">
                          {t("contact.form.subjects.general")}
                        </option>

                        <option value="membership">
                          {t("contact.form.subjects.membership")}
                        </option>

                        <option value="programs">
                          {t("contact.form.subjects.programs")}
                        </option>

                        <option value="partnerships">
                          {t("contact.form.subjects.partnerships")}
                        </option>

                        <option value="media">
                          {t("contact.form.subjects.media")}
                        </option>

                        <option value="other">
                          {t("contact.form.subjects.other")}
                        </option>
                      </select>
                    </div>

                    <div>
                      <label className="block font-paragraph font-semibold text-foreground mb-2">
                        {t("contact.form.fields.organization")}
                      </label>

                      <Input
                        name="organisation"
                        placeholder={t(
                          "contact.form.placeholders.organization",
                        )}
                      />
                    </div>

                    <div>
                      <label className="block font-paragraph font-semibold text-foreground mb-2">
                        {t("contact.form.fields.message")}
                      </label>

                      <Textarea
                        name="message"
                        required
                        rows={5}
                        placeholder={t("contact.form.placeholders.message")}
                      />
                    </div>

                    <div>
                      <label className="block font-paragraph font-semibold text-foreground mb-2">
                        {t("contact.form.fields.source")}
                      </label>

                      <select
                        name="source"
                        className="w-full p-3 border rounded-lg"
                      >
                        <option value="">
                          {t("contact.form.source.select")}
                        </option>

                        <option value="social-media">
                          {t("contact.form.source.social")}
                        </option>

                        <option value="referral">
                          {t("contact.form.source.referral")}
                        </option>

                        <option value="event">
                          {t("contact.form.source.event")}
                        </option>

                        <option value="partner">
                          {t("contact.form.source.partner")}
                        </option>

                        <option value="other">
                          {t("contact.form.source.other")}
                        </option>
                      </select>
                    </div>

                    <Button
                      type="submit"
                      disabled={status === "loading"}
                      className={`w-full text-white 
                        ${status === "loading" ? "bg-gray-500 cursor-not-allowed" : "bg-primary hover:bg-primary/90"}
                      `}
                    >
                      {status === "loading"
                        ? t("contact.form.sending")
                        : t("contact.form.send")}
                    </Button>

                    {status === "success" && (
                      <p className="text-green-600 font-semibold text-center">
                        {t("contact.form.success")}
                      </p>
                    )}

                    {status === "error" && (
                      <p className="text-red-600 font-semibold text-center">
                        {t("contact.form.error")}
                      </p>
                    )}
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-foreground text-background">
        <div className="container max-w-[100rem] mx-auto px-6">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl sm:text-5xl font-bold mb-6">
              {t("contact.faq.title1")}{" "}
              <span className="text-electric-blue">
                {t("contact.faq.highlight")}
              </span>
            </h2>

            <p className="font-paragraph text-lg sm:text-xl text-background/80 max-w-3xl mx-auto">
              {t("contact.faq.description")}
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 0 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="bg-background text-foreground p-6">
                  <CardContent className="p-0">
                    <h3 className="font-heading text-lg font-bold text-foreground mb-3">
                      {faq.question}
                    </h3>
                    <p className="font-paragraph text-foreground/80">
                      {faq.answer}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0, x: 0 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="font-paragraph text-background/80 mb-4">
              {t("contact.faq.noAnswer")}
            </p>

            <Button
              className="border-background text-background hover:bg-background hover:text-foreground"
              onClick={() => {
                const target = document.getElementById("contact-form");
                if (target) {
                  target.scrollIntoView({ behavior: "smooth" });
                }
              }}
            >
              {t("contact.faq.contactDirect")}
            </Button>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-blue-50 ">
        <div className="container max-w-[100rem] mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: 0 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-6">
                {t("contact.social.title1")}{" "}
                <span className="text-primary">
                  <br></br>
                  {t("contact.social.highlight")}
                </span>
              </h2>

              <p className="font-paragraph text-base sm:text-lg text-foreground/80 mb-10">
                {t("contact.social.description")}
              </p>

              <div className="space-y-4">
                {socialLinks.map((social, index) => (
                  <motion.div
                    key={index}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-background rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 gap-4 sm:gap-0"
                    initial={{ opacity: 0, x: 0 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className="flex items-center">
                      <Globe className="h-5 w-5 text-primary mr-3" />
                      <span className="font-paragraph font-semibold text-foreground">
                        {social.name}
                      </span>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
                      {/* <span className="font-paragraph text-sm text-foreground/60">
                        {social.followers} followers
                      </span> */}

                      <Button
                        size="sm"
                        className="border border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300 rounded-lg px-4 py-1"
                        asChild
                      >
                        <a
                          href={social.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t("contact.social.follow")}
                        </a>
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 0 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-6">
                {t("contact.office.title1")}{" "}
                <span className="text-secondary">
                  {t("contact.office.highlight")}
                </span>
              </h2>

              <p className="font-paragraph text-base sm:text-lg text-foreground/80 mb-8">
                {t("contact.office.description")}
              </p>

              <Card className="p-6 mt-20">
                <CardContent className="p-0">
                  <div className="flex flex-col sm:flex-row items-start mb-4">
                    <MapPin className="h-6 w-6 text-primary mr-0 sm:mr-3 mb-2 sm:mb-0" />
                    <div>
                      <h4 className="font-heading font-semibold text-foreground mb-2">
                        {t("contact.office.address")}
                      </h4>
                      <p className="font-paragraph text-foreground/80 break-words">
                        Oulmes , Maroc
                        <br />
                        Dar Echabab , دار الشباب
                        <br />
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-start">
                    <Clock className="h-6 w-6 text-secondary mr-0 sm:mr-3 mb-2 sm:mb-0" />
                    <div>
                      <h4 className="font-heading font-semibold text-foreground mb-2">
                        {t("contact.office.hoursTitle")}
                      </h4>
                      <p className="font-paragraph text-foreground/80">
                        {t("contact.info.hours.week")}
                        <br />
                        <br />
                        {t("contact.info.hours.sunday")}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
